import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'

import { config } from '@/config'

import { createTokenProvider } from './tokenProvider'

/**
 * MSAL wiring: authorization-code flow with PKCE by redirect.
 *
 * Cache: sessionStorage. Tokens then live only as long as the tab, so a
 * token is not left on disk for a script (or the next person at a shared
 * PC) to read, at the cost of a silent SSO round-trip when a new tab is
 * opened — Entra's own session cookie makes that invisible. localStorage
 * would only be worth it if sharing the session across tabs mattered more.
 *
 * Loaded with a dynamic import so dev mode (no Entra configured) does not
 * pull the library into the bundle it executes.
 */

export interface AuthClient {
  /** Completes a pending redirect and picks the active account. */
  init(): Promise<{ account: AccountInfo | null; error: string | null }>
  getToken: ReturnType<typeof createTokenProvider>
  /** Redirects to Microsoft; `redirectTo` is the in-app path to land on afterwards. */
  login(redirectTo?: string): Promise<void>
  logout(): Promise<void>
  account(): AccountInfo | null
}

export async function createAuthClient(): Promise<AuthClient> {
  const msal = await import('@azure/msal-browser')

  const app: IPublicClientApplication = await msal.createStandardPublicClientApplication({
    auth: {
      clientId: config.entraClientId,
      authority: `https://login.microsoftonline.com/${config.entraTenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: 'sessionStorage' },
  })

  const scopes = [config.entraApiScope]
  let active: AccountInfo | null = null

  const account = () => active ?? app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null

  const getToken = createTokenProvider({
    hasAccount: () => account() !== null,
    acquireTokenSilent: ({ forceRefresh }) =>
      app.acquireTokenSilent({ account: account()!, scopes, forceRefresh }),
    acquireTokenRedirect: () => app.acquireTokenRedirect({ account: account()!, scopes }),
    isInteractionRequired: (error) =>
      error instanceof msal.InteractionRequiredAuthError ||
      (error instanceof msal.BrowserAuthError && error.errorCode === 'no_account_error'),
  })

  return {
    async init() {
      let error: string | null = null
      try {
        const result = await app.handleRedirectPromise()
        if (result?.account) active = result.account
      } catch (cause) {
        error = cause instanceof Error ? cause.message : String(cause)
      }
      const found = account()
      if (found) app.setActiveAccount(found)
      return { account: found, error }
    },
    getToken,
    async login(redirectTo) {
      await app.loginRedirect({
        scopes,
        redirectStartPage: redirectTo ? window.location.origin + redirectTo : undefined,
      })
    },
    async logout() {
      await app.logoutRedirect({ account: account() ?? undefined })
    },
    account,
  }
}
